/**
 * Shared box grouping utilities for warehouse pages.
 *
 * Groups flat box lists by order for display in Incoming/Dispatch sections.
 */

import { EnhancedOrderBoxWithContext, DpdShipmentSummary } from '../types/logistics';

export interface OrderGroup {
  orderId: number;
  customerName: string;
  driverName: string;
  boxes: EnhancedOrderBoxWithContext[];
  dpdShipment?: DpdShipmentSummary;
}

/**
 * Groups boxes by orderId, attaching customer and driver names.
 *
 * @param driverField - Which driver ID to use for name lookup
 *   ('collectionDriverId' for Incoming, 'deliveryDriverId' for Dispatch)
 */
export function groupBoxesByOrder(
  boxes: EnhancedOrderBoxWithContext[],
  getDriverName: (id: number | null) => string,
  driverField: 'collectionDriverId' | 'deliveryDriverId' = 'collectionDriverId',
  dpdShipments?: Record<number, DpdShipmentSummary>
): OrderGroup[] {
  const groups = new Map<number, OrderGroup>();
  boxes.forEach(box => {
    if (!groups.has(box.orderId)) {
      const dpd = dpdShipments?.[box.orderId];
      groups.set(box.orderId, {
        orderId: box.orderId,
        customerName: box.customerName || `Order #${box.orderId}`,
        driverName: dpd ? 'DPD Delivery' : getDriverName(box[driverField] ?? box.assignedDriverId),
        boxes: [],
        dpdShipment: dpd,
      });
    }
    groups.get(box.orderId)!.boxes.push(box);
  });
  return Array.from(groups.values());
}
